import React from 'react';
import { colors, typography, radius, spacing } from '../../../theme';
import { getMovementCategoryColor } from '../../../constants/exerciseCategories';

const VARIANT_META = {
  improvement: { titleColor: colors.success, deltaColor: colors.success },
  attention: { titleColor: colors.danger, deltaColor: colors.danger },
  baseline: { titleColor: colors.textMuted, deltaColor: colors.textMuted },
};

/**
 * Shared row renderer for Strength Progress's three tiers. Brought back the
 * old StrengthDashboard.jsx's bar-based row treatment for that "analytic"
 * feel, organised into the new three-tier structure instead of one long
 * table. Bar fill uses the TIER's semantic colour (green/red/gold-muted),
 * not the exercise's muscle-group category colour — the old design used
 * category colour for bars, but that clashes here: Chest's category colour
 * is the same green used for "improvement" and Shoulders' is the same red
 * used for "attention," so a Shoulders exercise sitting in Top Improvements
 * would render a red bar next to a green ↑ badge. Category colour is kept
 * only as the small left strip, which is a purely contextual "which muscle
 * group" indicator rather than the primary signal.
 * props.items: array of { exerciseName, estimated1RM, movementCategory, increasePercent? }
 * props.maxRM: shared scale across all tiers so bar lengths are comparable
 * props.showRange: opt-in extra line — lowest/highest weight actually
 * logged plus most recent session date. Off by default; both the client's
 * Stats > Progress view and the coach Performance page now pass true.
 */
const StrengthTierList = ({ title, items, variant, maxRM, showRange = false }) => {
  if (!items || items.length === 0) return null;
  const meta = VARIANT_META[variant];

  return (
    <div style={{ marginBottom: spacing.lg }}>
      <div style={{ ...typography.label, color: meta.titleColor, marginBottom: spacing.sm }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
        {items.map((item) => {
          const categoryColor = getMovementCategoryColor(item.movementCategory);
          const barPct = maxRM > 0 ? Math.max(4, (item.estimated1RM / maxRM) * 100) : 4;
          const badgeText = variant === 'baseline'
            ? 'Baseline'
            : `${item.increasePercent > 0 ? '↑' : '↓'} ${Math.abs(item.increasePercent)}%`;

          return (
            <div key={item.exerciseName} style={styles.row}>
              <div style={{ width: 3, borderRadius: 4, backgroundColor: categoryColor, alignSelf: 'stretch', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: spacing.sm }}>
                  <span style={{ ...typography.body, color: colors.text, fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.exerciseName}
                  </span>
                  <span style={{ ...typography.caption, fontWeight: '700', color: meta.deltaColor, flexShrink: 0 }}>
                    {badgeText}
                  </span>
                </div>
                <div style={styles.barTrack}>
                  <div style={{ ...styles.barFill, width: `${barPct}%`, background: `linear-gradient(90deg, ${meta.deltaColor}CC, ${meta.deltaColor})` }} />
                </div>
                <div style={{ ...typography.caption, color: colors.textMuted, marginTop: '3px' }}>
                  {item.estimated1RM} kg estimated 1RM
                  {item.increaseKg != null && variant !== 'baseline' && (
                    <span> · {item.increaseKg > 0 ? '+' : ''}{item.increaseKg} kg</span>
                  )}
                </div>
                {showRange && (item.minWeight != null || item.lastSessionDate) && (
                  <div style={{ ...typography.caption, color: colors.textSubtle, marginTop: '2px' }}>
                    {item.minWeight != null && item.maxWeight != null && (
                      <span>{item.minWeight}–{item.maxWeight} kg range</span>
                    )}
                    {item.lastSessionDate && (
                      <span>{item.minWeight != null ? ' · ' : ''}Last {new Date(item.lastSessionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const styles = {
  row: {
    display: 'flex',
    alignItems: 'stretch',
    gap: spacing.sm,
    padding: `${spacing.sm} ${spacing.md}`,
    backgroundColor: colors.surface2,
    borderRadius: radius.sm,
    border: `1px solid ${colors.border}`,
  },
  barTrack: {
    marginTop: '6px',
    height: '6px',
    borderRadius: '4px',
    backgroundColor: 'rgba(255,255,255,0.06)',
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.4s ease',
  },
};

export default StrengthTierList;
